import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Copy, Check, ArrowRight, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const PILLS = ["peace", "healing", "provision", "my children", "boldness"];

export function TryDecree() {
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async (t) => {
    const value = (t ?? topic).trim();
    if (!value) { toast.error("Type a topic first."); return; }
    setTopic(value);
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/decrees/generate", { topic: value, audience: "self" });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    const text = result.decrees.map((d, i) => `${i + 1}. ${d.text} (${d.reference})`).join("\n\n");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16" data-testid="try-decree-section">
      <div className="border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-6 sm:p-10 lg:p-12 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-[#9B7E4E] flex items-center gap-2"><Wand2 className="w-4 h-4" /> Try it right now</p>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] tracking-tight mt-3">Decree one thing.</h2>
          <p className="font-body text-base text-[#4A4744] mt-3 leading-relaxed">Enter what you're facing or believing for. You'll get five real, scripture-anchored decrees in about ten seconds — no account needed.</p>
          <form onSubmit={(e) => { e.preventDefault(); run(); }} className="mt-6 flex flex-col sm:flex-row gap-3">
            <input
              data-testid="try-decree-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              maxLength={80}
              placeholder="e.g. peace in my home"
              className="flex-1 bg-[#FAF9F6] border border-[#D9D4CC] rounded-sm px-4 py-3.5 font-body text-base text-[#2C2A29] placeholder:text-[#B7AE9F] focus:outline-none focus:ring-2 focus:ring-[#9B7E4E] transition-shadow duration-300"
            />
            <button type="submit" data-testid="try-decree-submit-button" disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 active:scale-[0.99] transition-all duration-200 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Decree it
            </button>
          </form>
          <div className="flex flex-wrap gap-2 mt-4">
            {PILLS.map((p) => (
              <button key={p} type="button" data-testid={`try-decree-pill-${p.replace(/\s+/g, "-")}`} onClick={() => run(p)} disabled={loading}
                className="px-3.5 py-1.5 text-sm border border-[#D9D4CC] rounded-full text-[#4A4744] hover:bg-[#EAE6DF] hover:border-[#B7AE9F] transition-colors duration-300 disabled:opacity-50">
                {p}
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7 lg:border-l lg:border-[#EAE6DF] lg:pl-10 min-h-[14rem]">
          {loading && (
            <div className="h-full flex flex-col items-center justify-center py-12 text-center" data-testid="try-decree-loading">
              <Loader2 className="w-7 h-7 text-[#2C2A29] animate-spin" />
              <p className="font-decree text-lg text-[#7A756E] mt-4">Searching the Scriptures for “{topic}”…</p>
            </div>
          )}
          {!loading && !result && (
            <div className="h-full flex flex-col justify-center py-6" data-testid="try-decree-placeholder">
              <p className="font-decree italic text-xl sm:text-2xl text-[#2C2A29] leading-snug">“I decree that the peace of God, which surpasses all understanding, guards my heart and my mind in Christ Jesus.”</p>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-[#9B7E4E] mt-3">Philippians 4:7 · sample</p>
              <p className="font-body text-sm text-[#7A756E] mt-6">Your five decrees will appear here.</p>
            </div>
          )}
          {!loading && result && (
            <div data-testid="try-decree-result-box" className="animate-fade-up">
              <div className="flex items-center justify-between gap-3">
                <p className="font-heading text-xl text-[#2C2A29] capitalize">{result.topic}</p>
                <button data-testid="try-decree-copy-button" onClick={copy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#D9D4CC] rounded-sm text-[#4A4744] hover:bg-[#EAE6DF] transition-colors duration-300">
                  {copied ? <Check className="w-3.5 h-3.5 text-[#9B7E4E]" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <ol className="mt-4 space-y-4">
                {result.decrees.map((d, i) => (
                  <li key={i} data-testid={`try-decree-item-${i}`} className="flex gap-4">
                    <span className="font-heading text-lg text-[#B7AE9F] w-6 shrink-0">{i + 1}</span>
                    <div>
                      <p className="font-decree text-lg text-[#2C2A29] leading-relaxed">{d.text}</p>
                      <p className="font-body text-xs uppercase tracking-wide text-[#9B7E4E] mt-1">{d.reference}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-8 pt-6 border-t border-[#EAE6DF] flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="font-body text-sm text-[#4A4744] flex-1">That's 5 of up to 15. Create a free account to get the full set, decree over others, save your history and listen aloud.</p>
                <Link to="/auth" data-testid="try-decree-signup-button" className="inline-flex items-center gap-2 px-5 py-3 bg-[#9B7E4E] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 text-sm">
                  Unlock the full set <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
